import React, { useEffect, useState } from "react"
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Label,
  Button,
} from "reactstrap"
import { Formik, Form, Field } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import { addUserSchemas, editUserSchemas } from "components/Common/validation"
import { EMAIL_TOTAL_MAX_LENGTH } from "utils/emailValidation"
import { useLocation, useNavigate } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import {
  addAdminUser,
  getAdminUserDetails,
  getAllRoles,
  updateAdminUser,
} from "store/actions/usersAction"
import Spinners from "components/Shared/Spinners"
import isPermissionEnabled from "common/isPermissionEnabled"
import { showAlert } from "store/reducers/alertReducer"
import Select from "react-select"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import Switch from "components/Shared/Switch"
import FormikPhoneNumberField from "components/Shared/FormikPhoneNumberField"
import { normalizePhoneValue } from "utils/phoneUtils"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"

const BASE_PATH = "/superadmin/users"

const roleSelectMenuProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
}

const roleSelectStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: base => ({ ...base, zIndex: 9999 }),
  menuList: base => ({
    ...base,
    maxHeight: 240,
    overflowY: "auto",
    paddingBottom: 4,
  }),
}

const initialValues = {
  firstName: "",
  lastName: "",
  username: "",
  password: "",
  email: "",
  mobile: "",
  roles: "",
  locations: [],
  allowExports: false,
}

const SuperAdminAddUser = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} Add User | Super Admin`

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const [formValues, setFormValues] = useState(initialValues)
  const [showPassword, setShowPassword] = useState(false)

  const roleOptionLabel = role =>
    language === "sa"
      ? role?.nameAr || role?.name || "-"
      : role?.name || "-"

  const { loading, isLoading, actionType, rolesList, userDetails } = useSelector(
    state => state.Users
  )
  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      103,
      "sa_users_2",
      accountType,
      accountScope
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      103,
      "sa_users_3",
      accountType,
      accountScope
    )
    if ((mode === "add" && !isPermissionCreate) || (mode === "edit" && !isPermissionEdit)) {
      navigateToAccessibleRoute(navigate)
    }
  }, [mode, userRole, accountType, accountScope, isSuperAdminSession, navigate])

  useEffect(() => {
    if (mode === "edit") {
      dispatch(getAdminUserDetails({ id }))
    }
  }, [])

  useEffect(() => {
    dispatch(getAllRoles())
  }, [])

  useEffect(() => {
    if (userDetails && mode === "edit") {
      const {
        firstName,
        lastName,
        email,
        phoneNumber,
        role,
        username,
        allowExports,
      } = userDetails
      setFormValues(prevVal => ({
        ...prevVal,
        firstName,
        lastName,
        username,
        email,
        mobile: normalizePhoneValue(phoneNumber),
        roles: {
          label: roleOptionLabel(role),
          value: role?._id,
        },
        locations: [],
        allowExports,
      }))
    }
  }, [userDetails, language])

  const handleOnSubmit = values => {
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phoneNumber: values.mobile,
      password: values.password,
      role: values.roles.value,
      locations: [],
      accountScope: "super_admin",
      allowExports: values.allowExports,
    }

    if (mode === "add") {
      dispatch(addAdminUser({ ...payload, navigate })).then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        dispatch(showAlert({ text: msg, type: statusCode === 200 && status ? "success" : "error" }))
      })
      return
    }

    dispatch(
      updateAdminUser({
        ...payload,
        id,
      })
    ).then(action => {
      const { statusCode, status, msg } = action?.payload || {}
      if (statusCode === 200 && status) {
        dispatch(showAlert({ text: msg, type: "success" }))
        navigate(-1)
      } else {
        dispatch(showAlert({ text: msg, type: "error" }))
      }
    })
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content edit_user_page">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  {mode === "add" ? t("Add") : t("Edit")} {t("User")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize
                  validationSchema={
                    mode === "add" ? addUserSchemas(true) : editUserSchemas(true)
                  }
                  validateOnChange
                  validateOnBlur
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, setFieldTouched, errors, touched }) => (
                    <Form>
                      <Row className="mb-4">
                        <Col md={6}>
                          <div className="form_field">
                            <Label htmlFor="firstName" className="col col-form-label">
                              <span className="text-danger">*</span> {t("First Name")}
                            </Label>
                            <Field
                              name="firstName"
                              type="text"
                              id="firstName"
                              placeholder={t("First Name")}
                              className={`form-control ${touched.firstName && errors.firstName ? "is-invalid" : ""}`}
                            />
                            <TextError name="firstName" />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form_field">
                            <Label htmlFor="lastName" className="col col-form-label">
                              <span className="text-danger">*</span> {t("Last Name")}
                            </Label>
                            <Field
                              name="lastName"
                              type="text"
                              id="lastName"
                              placeholder={t("Last Name")}
                              className={`form-control ${touched.lastName && errors.lastName ? "is-invalid" : ""}`}
                            />
                            <TextError name="lastName" />
                          </div>
                        </Col>
                        <Col md={6}>
                          <Label className="col col-form-label">
                            <span className="text-danger">*</span> {t("Roles")}
                          </Label>
                          <div className="mt-1">
                            <Select
                              value={values.roles}
                              onChange={item => setFieldValue("roles", item)}
                              options={(rolesList || []).map(item => ({
                                label: roleOptionLabel(item),
                                value: item._id,
                              }))}
                              className={`select2-selection ${touched.roles && errors.roles ? "danger_color_border" : ""}`}
                              placeholder={t("Roles")}
                              {...roleSelectMenuProps}
                              styles={roleSelectStyles}
                            />
                          </div>
                          <TextError name="roles" />
                        </Col>
                        {mode === "add" && (
                          <Col sm={6}>
                            <div className="form_field">
                              <Label htmlFor="password" className="col col-form-label">
                                <span className="text-danger">*</span> {t("Password")}
                              </Label>
                              <div className="position-relative">
                                <Field
                                  type={showPassword ? "text" : "password"}
                                  name="password"
                                  id="password"
                                  autoComplete="new-password"
                                  placeholder={t("Password")}
                                  onChange={e => {
                                    setFieldValue(
                                      "password",
                                      e.target.value,
                                      true
                                    )
                                    setFieldTouched("password", true, false)
                                  }}
                                  className={`form-control ${touched.password && errors.password ? "is-invalid" : ""}`}
                                />
                                <i
                                  onClick={() => setShowPassword(!showPassword)}
                                  className={`bx bx-${showPassword ? "show-alt" : "hide"} common_eye`}
                                ></i>
                              </div>
                              <TextError name="password" />
                            </div>
                          </Col>
                        )}
                        <Col sm={6}>
                          <div className="form_field">
                            <Label htmlFor="email" className="col col-form-label">
                              <span className="text-danger">*</span> {t("Email")}
                            </Label>
                            <Field
                              type="email"
                              name="email"
                              id="email"
                              maxLength={EMAIL_TOTAL_MAX_LENGTH}
                              autoComplete="off"
                              placeholder={t("Email")}
                              className={`form-control ${touched.email && errors.email ? "is-invalid" : ""}`}
                            />
                            <TextError name="email" />
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="form_field">
                            <Label htmlFor="mobile" className="col col-form-label">
                              {t("Phone Number")}
                            </Label>
                            <FormikPhoneNumberField
                              name="mobile"
                              id="mobile"
                              placeholder={t("Phone Number")}
                            />
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="form_field">
                            <Label htmlFor="allowExports" className="col col-form-label">
                              {t("Allow Exports")}
                            </Label>
                            <Switch
                              onClickHandler={() =>
                                setFieldValue("allowExports", !values.allowExports)
                              }
                              isChecked={values.allowExports}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row className="justify-content-end my-5">
                        <Col sm={12}>
                          <div className="d-flex justify-content-end">
                            <Button type="button" color="secondary" onClick={() => navigate(-1)}>
                              {t("Cancel")}
                            </Button>
                            <Button className="mx-2" type="submit" color="success" disabled={isLoading}>
                              {mode === "add" ? t("Add") : t("Save")}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default SuperAdminAddUser
